import { useState, useEffect, useReducer, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { ArrowLeft, Save, Plus, Loader2, Sparkles, X, DiamondPlus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { resumeReducer } from "@/hooks/useResumeReducer";
import { resumeInitialState } from "@/lib/resumeInitialState";
import { DynamicListSection } from "@/components/DynamicListSection";
import { ResumeField } from '@/components/ResumeField';
import { ResumeSection } from '@/components/ResumeSection';
import { countries } from '@/utils/countries';
import { AutoTranslate } from "@/lib/autoTranslate.tsx";
import { skillsRecommendations } from "@/hooks/skills-recommendations";

export default function ResumeEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [formatting, setFormatting] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useReducer(resumeReducer, resumeInitialState);
  const [isContentLoaded, setIsContentLoaded] = useState(false);
  const formattingOptions = AutoTranslate(`Formatting Options`);
  const resumeTitle = AutoTranslate(`Resume Title`);
  const [skillsRecommendationsList, setSkillsRecommendationsList] = useState<
    Array<{ name: string; skill_type: string | null }>
  >([]);
  const [isRecommendationLoading, setIsRecommendationLoading] = useState(false);
  const [debouncedJobTitle, setDebouncedJobTitle] = useState("");

  useEffect(() => {
    if (id) {
      fetchResume();
    }
  }, [id]);

  const fetchResume = async () => {
    if (!id || !user) return;

    try {
      const { data, error } = await supabase
        .from('saved_resumes')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)
        .single();

      if (error) throw error;

      if (data) {
        const resumeContent = typeof data.content === "string"
          ? JSON.parse(data.content)
          : data.content;

        const fetchedFormatting = data.formatting || {};  // formatting options are stored here

        setTitle(data.title || '');
        setFormatting(fetchedFormatting);
        setContent({
          type: "SET_FULL_RESUME",
          value: {
            ...resumeInitialState,
            ...resumeContent,

            // Force all arrays to always be arrays:
            softSkills: resumeContent.softSkills ?? [],
            technicalSkills: resumeContent.technicalSkills ?? [],
            education: resumeContent.education ?? [],
            workExperience: resumeContent.workExperience ?? [],
            certifications: resumeContent.certifications ?? [],
            referees: resumeContent.referees ?? [],
            languages: resumeContent.languages ?? [],
          }
        });
        setIsContentLoaded(true);
        console.log("content is ready, fetched content:", resumeContent);
      }
    } catch (error) {
      toast.error('Failed to load resume');
      navigate('/saved-resumes');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!id || !user) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('saved_resumes')
        .update({
          title,
          content: JSON.parse(JSON.stringify(content)),
          formatting,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      toast.success('Resume saved successfully');
      navigate('/saved-resumes');
    } catch (error) {
      toast.error('Failed to save resume');
    } finally {
      setSaving(false);
    }
  };

  const fetchSkillsRecommendations = async (jobTitle: string) => {
    console.log("Fetching skills recommendations for job title:", jobTitle);

    try {
      const recommendedSkills = await skillsRecommendations(jobTitle, [
        ...(content.softSkills || []),
        ...(content.technicalSkills || [])
      ]);

      setSkillsRecommendationsList(recommendedSkills);
      console.log("Recommended skills for job title", jobTitle, ":", recommendedSkills);
    } catch (error) {
      console.error("Failed to fetch skills recommendations:", error);
      setSkillsRecommendationsList([]);
    } finally {
      setIsRecommendationLoading(false);
    }
  };

  useEffect(() => { // Debounce job title input to avoid excessive calls to fetch recommendations while the user is typing
    const jobTitle = (content.jobTitle || "").trim();

    const timeout = setTimeout(() => {
      setDebouncedJobTitle(jobTitle);
    }, 700);

    return () => clearTimeout(timeout);
  }, [content.jobTitle]);

  const lastFetchedTitleRef = useRef("");

  useEffect(() => {
    if (!isContentLoaded) return;

    const title = debouncedJobTitle.trim();
    if (!title || title.length < 3) return;
    if (lastFetchedTitleRef.current === title.toLowerCase()) return;

    lastFetchedTitleRef.current = title.toLowerCase();
    setIsRecommendationLoading(true);
    setSkillsRecommendationsList([]);
    fetchSkillsRecommendations(title);
  }, [debouncedJobTitle, isContentLoaded]); // Only fetch recommendations when the debounced job title changes and after the initial content has loaded

  const normalizeSkillType = (t: string | null | undefined) => (t || "").trim().toLowerCase();

  const softRecommended = skillsRecommendationsList.filter((s) =>
    normalizeSkillType(s.skill_type).includes("soft")
  );

  const technicalRecommended = skillsRecommendationsList.filter((s) => {
    const st = normalizeSkillType(s.skill_type);
    return st.includes("tech") || st.includes("technical");
  });

  const otherRecommended = skillsRecommendationsList.filter((s) => {
    const st = normalizeSkillType(s.skill_type);
    return !st.includes("soft") && !(st.includes("tech") || st.includes("technical"));
  });

  const hasSoftSkill = (name: string) =>
    (content.softSkills || []).some(
      (x: string) => (x || "").trim().toLowerCase() === name.trim().toLowerCase()
    );

  const hasTechnicalSkill = (name: string) =>
    (content.technicalSkills || []).some(
      (x: string) => (x || "").trim().toLowerCase() === name.trim().toLowerCase()
    );

  const addSoftSkill = (name: string) => {
    if (!name || hasSoftSkill(name)) return;
    setContent({
      type: "ADD_ITEM",
      path: "softSkills",
      value: name,
    });
  };

  const addTechnicalSkill = (name: string) => {
    if (!name || hasTechnicalSkill(name)) return;
    setContent({
      type: "ADD_ITEM",
      path: "technicalSkills",
      value: name,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-main" role="status">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-main p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/saved-resumes')}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Saved Resumes
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="gap-2"
          >
            <Save className="h-4 w-4" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Editor Toolbar */}
          <Card className="p-6 h-fit bg-card/80 backdrop-blur-sm">
            <h2 className="text-lg font-semibold mb-4">{formattingOptions}</h2>

            <div className="space-y-4">
              <div>
                <Label htmlFor="title">{resumeTitle}</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="font">Font Family</Label>
                <Select value={formatting.fontFamily || ""} onValueChange={(value) => setFormatting({
                  ...formatting,
                  fontFamily: value
                })}>
                  <SelectTrigger id="font" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Arial">Arial</SelectItem>
                    <SelectItem value="Times New Roman">Times New Roman</SelectItem>
                    <SelectItem value="Helvetica">Helvetica</SelectItem>
                    <SelectItem value="Georgia">Georgia</SelectItem>
                    <SelectItem value="Courier New">Courier New </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="fontSize">Font Size</Label>
                <Select value={formatting?.fontSize || ""} onValueChange={(value) => setFormatting({
                  ...formatting,
                  fontSize: value
                })}>
                  <SelectTrigger id="fontSize" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="12pt">12pt</SelectItem>
                    <SelectItem value="14pt">14pt</SelectItem>
                    <SelectItem value="16pt">16pt</SelectItem>
                    <SelectItem value="18pt">18pt</SelectItem>
                  </SelectContent>
                </Select>
                <div>
                  <Label htmlFor="layout">Page Layout</Label>
                  <Select value={formatting?.pageLayout || ""} onValueChange={(value) => setFormatting({
                    ...formatting,
                    pageLayout: value
                  })}>
                    <SelectTrigger id="layout" className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A4Landscape">A4 Landscape</SelectItem>
                      <SelectItem value="A4Portrait">A4 Portrait</SelectItem>
                      <SelectItem value="A3Portrait">A3 Portrait</SelectItem>
                      <SelectItem value="A3Landscape">A3 Landscape</SelectItem>
                      <SelectItem value="LetterPortrait">Letter Portrait</SelectItem>
                      <SelectItem value="LetterLandscape">Letter Landscape</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </Card>

          {/* Content Editor */}
          <Card className="lg:col-span-2 p-6 bg-card/80 backdrop-blur-sm">
            <h2 className="font-bold text-3xl text-center mb-4">Edit Content</h2>

            <div className="space-y-4">
              {content && (
                <div className="space-y-3">
                  <ResumeSection title="Personal Information">

                    {[
                      { label: "Full Name", path: "fullName" },
                      { label: "Email", path: "email" },
                      { label: "City", path: "city" },
                      { label: "Job Title", path: "jobTitle" },
                      { label: "LinkedIn URL", path: "linkedinUrl" },
                      { label: "Portfolio URL", path: "portfolioUrl" },
                      { label: "Years of Experience", path: "yearsExperience" },
                    ].map((field) => (
                      <ResumeField
                        key={field.path}
                        label={field.label}
                        value={content[field.path]}
                        path={field.path}
                        setContent={setContent}
                      />
                    ))}
                  </ResumeSection>

                  <div className='mb-3'>
                    <Label htmlFor="country">Phone Number</Label>
                    <div className="flex gap-2 mb-2">

                      {/* Country Dial Code Selector */}
                      <Select
                        value={content.countryCode || ""}
                        onValueChange={(value) =>
                          setContent({
                            type: "UPDATE_FIELD",
                            path: "countryCode",
                            value,
                          })
                        }
                      >
                        <SelectTrigger className="w-52">
                          <SelectValue placeholder="Code" />
                        </SelectTrigger>

                        <SelectContent>
                          {countries.map((country) => (
                            <SelectItem
                              key={country.code}
                              value={country.dial_code}  // FIXED: store +90, +49, +1, etc.
                            >
                              {country.name} ({country.dial_code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {/* Phone Number Input */}
                      <Input
                        className="flex-1"
                        id="phoneNumber"
                        value={content.phoneNumber || ""}
                        onChange={(e) =>
                          setContent({
                            type: "UPDATE_FIELD",
                            path: "phoneNumber",
                            value: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>


                  <ResumeSection
                    title="Summary Style"
                  >

                    {[
                      { label: "Career Focus", path: "summaryStyle.careerFocus" },
                      { label: "Personality", path: "summaryStyle.personality" },
                      { label: "Industry Tone", path: "summaryStyle.industryTone" },
                      { label: "Experience Level", path: "summaryStyle.experienceLevel" },
                    ].map((field) => (
                      <ResumeField
                        key={field.path}
                        label={field.label}
                        value={field.path.split('.').reduce((obj, key) => obj?.[key], content)}
                        path={field.path}
                        setContent={setContent}
                      />
                    ))}
                  </ResumeSection>

                  <DynamicListSection
                    title="Education"
                    path="education"
                    items={content.education}
                    onAdd={() =>
                      setContent({
                        type: "ADD_ITEM",
                        path: "education",
                        value: {
                          id: crypto.randomUUID(),
                          schoolName: "",
                          degree: "",
                          startMonth: "",
                          startYear: "",
                          endMonth: "",
                          endYear: "",
                        },
                      })
                    }
                    onRemove={(index) =>
                      setContent({
                        type: "REMOVE_ITEM",
                        path: "education",
                        index,
                      })
                    }
                    onUpdate={(path, value) =>
                      setContent({ type: "UPDATE_FIELD", path, value })
                    }
                    fields={[
                      { name: "schoolName", label: "School Name", row: 1 },
                      { name: "degree", label: "Degree", row: 1 },

                      { name: "startMonth", label: "Start Month", row: 2 },
                      { name: "startYear", label: "Start Year", row: 2 },

                      { name: "endMonth", label: "End Month", row: 3 },
                      { name: "endYear", label: "End Year", row: 3 },

                      { name: "awards", label: "Awards", component: "textarea", row: 4 },

                      { name: "grades", label: "Grades", row: 5 },

                      {
                        name: "project", label: "Project", component: "textarea", row: 6, formattingOption: true, formatting: formatting?.sectionStyles?.education?.project, setFormatting: (value) =>
                          setFormatting({
                            ...formatting,
                            sectionStyles: {
                              ...formatting.sectionStyles,
                              education: {
                                project: value
                              }
                            }
                          })
                      },
                    ]}
                  />

                  <DynamicListSection
                    title="Work Experience"
                    path="workExperience"
                    items={content.workExperience}
                    onAdd={() =>
                      setContent({
                        type: "ADD_ITEM",
                        path: "workExperience",
                        value: {
                          id: crypto.randomUUID(),
                          jobTitle: "",
                          companyName: "",
                          startMonth: "",
                          startYear: "",
                          endMonth: "",
                          endYear: "",
                          description: "",
                        },
                      })
                    }
                    onRemove={(index) =>
                      setContent({
                        type: "REMOVE_ITEM",
                        path: "workExperience",
                        index,
                      })
                    }
                    onUpdate={(path, value) =>
                      setContent({ type: "UPDATE_FIELD", path, value })
                    }
                    fields={[
                      { name: "jobTitle", label: "Job Title", row: 1 },
                      { name: "companyName", label: "Company Name", row: 1 },

                      { name: "startMonth", label: "Start Month", row: 2 },
                      { name: "startYear", label: "Start Year", row: 2 },

                      { name: "endMonth", label: "End Month", row: 3 },
                      { name: "endYear", label: "End Year", row: 3 },

                      {
                        name: "description", label: "Description", component: "textarea", row: 4, formattingOption: true, formatting: formatting?.sectionStyles?.workExperience?.description, setFormatting: (value) =>
                          setFormatting({
                            ...formatting,
                            sectionStyles: {
                              ...formatting.sectionStyles,
                              workExperience: {
                                description: value
                              }
                            }
                          })
                      },
                    ]}
                  />

                  <DynamicListSection
                    title="Certifications"
                    path="certifications"
                    items={content.certifications}
                    onAdd={() =>
                      setContent({
                        type: "ADD_ITEM",
                        path: "certifications",
                        value: {
                          id: crypto.randomUUID(),
                          name: "",
                          month: "",
                          year: "",
                        },
                      })
                    }
                    onRemove={(index) =>
                      setContent({
                        type: "REMOVE_ITEM",
                        path: "certifications",
                        index,
                      })
                    }
                    onUpdate={(path, value) =>
                      setContent({ type: "UPDATE_FIELD", path, value })
                    }
                    fields={[
                      { name: "name", label: "Certification Name", row: 1 },
                      { name: "month", label: "Month", row: 2 },
                      { name: "year", label: "Year", row: 2 },
                    ]}
                  />

                  <DynamicListSection
                    title="Languages"
                    path="languages"
                    items={content.languages}
                    onAdd={() =>
                      setContent({
                        type: "ADD_ITEM",
                        path: "languages",
                        value: {
                          id: crypto.randomUUID(),
                          name: "",
                          proficiency: "",
                        },
                      })
                    }
                    onRemove={(index) =>
                      setContent({
                        type: "REMOVE_ITEM",
                        path: "languages",
                        index,
                      })
                    }
                    onUpdate={(path, value) =>
                      setContent({ type: "UPDATE_FIELD", path, value })
                    }
                    fields={[
                      { name: "name", label: "Language", row: 1 },
                      { name: "proficiency", label: "Proficiency", row: 1, component: "select", options: ["Beginner", "Intermediate", "Advanced", "Fluent", "Native"] },
                    ]}
                  />
                  <DynamicListSection
                    title="Referees"
                    path="referees"
                    items={content.referees}
                    onAdd={() =>
                      setContent({
                        type: "ADD_ITEM",
                        path: "referees",
                        value: {
                          id: crypto.randomUUID(),
                          fullName: "",
                          email: "",
                          phoneNumber: "",
                          company: "",
                          position: "",
                        },
                      })
                    }
                    onRemove={(index) =>
                      setContent({
                        type: "REMOVE_ITEM",
                        path: "referees",
                        index,
                      })
                    }
                    onUpdate={(path, value) =>
                      setContent({ type: "UPDATE_FIELD", path, value })
                    }
                    fields={[
                      { name: "fullName", label: "Full Name", row: 1 },
                      { name: "email", label: "Email", row: 1 },
                      { name: "phoneNumber", label: "Phone Number", row: 1 },
                      { name: "company", label: "Company", row: 2 },
                      { name: "position", label: "Position", row: 2 },
                    ]}
                  />
                  <div className="flex justify-center border-b mb-4 items-center">
                    <h3 className="font-bold text-xl text-center mb-4">Soft Skills</h3>
                  </div>
                  {isRecommendationLoading && (
                    <div className="mb-4 overflow-hidden rounded-xl border border-primary/10 bg-gradient-to-r from-muted/60 to-muted/30 p-4 shadow-sm" role="status">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-background/90 shadow-sm">
                          <Loader2 className="h-5 w-5 animate-spin text-primary" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-primary" />
                            <p className="text-sm font-semibold text-foreground">Finding soft skill recommendations</p>
                          </div>
                          <p className="text-xs text-muted-foreground">Matching suggestions to the selected role…</p>
                        </div>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2">
                        <div className="h-8 w-24 animate-pulse rounded-full bg-background/80" />
                        <div className="h-8 w-32 animate-pulse rounded-full bg-background/80" />
                        <div className="h-8 w-28 animate-pulse rounded-full bg-background/80" />
                        <div className="h-8 w-20 animate-pulse rounded-full bg-background/80" />
                      </div>
                    </div>
                  )}
                  {!isRecommendationLoading && softRecommended.length === 0 && (
                    <p className="text-sm text-muted-foreground mb-3">No soft skill recommendations found.</p>
                  )}
                  {softRecommended.length > 0 && !isRecommendationLoading && (
                    <div className="mb-4 overflow-hidden rounded-xl border border-primary/10 bg-gradient-to-r from-muted/60 to-muted/30 p-4 shadow-sm">
                      <p className="text-xs font-medium text-muted-foreground mb-2">Recommended</p>
                      <div className="flex flex-wrap gap-2">
                        {softRecommended.map((s) => {
                          const disabled = hasSoftSkill(s.name);
                          return (
                            <Button
                              key={`rec-soft-${s.name}`}
                              type="button"
                              size="sm"
                              variant={disabled ? "secondary" : "outline"}
                              disabled={disabled}
                              className="h-8 px-3 text-xs font-medium gap-1 rounded-full"
                              onClick={() => addSoftSkill(s.name)}
                              title={disabled ? "Already added" : "Add to Soft Skills"}
                            >
                              <Plus className="h-3.5 w-3.5" />
                              {s.name}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {content.softSkills?.map((skill, index) => (
                    <div key={index} className="flex items-center gap-2 mb-2 ">

                      <div className="flex-1">
                        <ResumeField
                          label=""
                          value={skill}
                          path={`softSkills.${index}`}
                          setContent={setContent}
                        />
                      </div>

                      <Button variant="ghost" size="icon" className="h-6 w-6" aria-label="Remove skill" onClick={() =>
                        setContent({
                          type: "REMOVE_ITEM",
                          path: "softSkills",
                          index
                        })
                      }>
                        <X className="h-6 w-6" />
                      </Button>
                    </div>
                  ))}

                  <Button
                    className="mt-2"
                    onClick={() =>
                      setContent({
                        type: "ADD_ITEM",
                        path: "softSkills",
                        value: "",
                      })
                    }
                  >
                    <DiamondPlus className="h-5 w-5" />
                    Add Soft Skill
                  </Button>

                  <div className="flex justify-center border-b-2 mb-4 items-center mt-6">
                    <h3 className="font-bold text-xl text-center mb-4">Technical Skills</h3>

                  </div>
                  {isRecommendationLoading && (
                    <div className="mb-4 overflow-hidden rounded-xl border border-primary/10 bg-gradient-to-r from-muted/60 to-muted/30 p-4 shadow-sm" role="status">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-background/90 shadow-sm">
                          <Loader2 className="h-5 w-5 animate-spin text-primary" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-primary" />
                            <p className="text-sm font-semibold text-foreground">Finding technical skill recommendations</p>
                          </div>
                          <p className="text-xs text-muted-foreground">Matching suggestions to the selected role…</p>
                        </div>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2">
                        <div className="h-8 w-24 animate-pulse rounded-full bg-background/80" />
                        <div className="h-8 w-32 animate-pulse rounded-full bg-background/80" />
                        <div className="h-8 w-28 animate-pulse rounded-full bg-background/80" />
                        <div className="h-8 w-20 animate-pulse rounded-full bg-background/80" />
                      </div>
                    </div>
                  )}
                  {!isRecommendationLoading && technicalRecommended.length === 0 && (
                    <p className="text-sm text-muted-foreground mb-3">No technical skill recommendations found.</p>
                  )}
                  {technicalRecommended.length > 0 && !isRecommendationLoading && (
                    <div className="mb-4 overflow-hidden rounded-xl border border-primary/10 bg-gradient-to-r from-muted/60 to-muted/30 p-4 shadow-sm">
                      <p className="text-xs font-medium text-muted-foreground mb-2">Recommended</p>
                      <div className="flex flex-wrap gap-2">
                        {technicalRecommended.map((s) => {
                          const disabled = hasTechnicalSkill(s.name);
                          return (
                            <Button
                              key={`rec-tech-${s.name}`}
                              type="button"
                              size="sm"
                              variant={disabled ? "secondary" : "outline"}
                              disabled={disabled}
                              className="h-8 px-3 text-xs font-medium gap-1 rounded-full"
                              onClick={() => addTechnicalSkill(s.name)}
                              title={disabled ? "Already added" : "Add to Technical Skills"}
                            >
                              <Plus className="h-3.5 w-3.5" />
                              {s.name}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {content.technicalSkills?.map((skill, index) => (
                    <div key={index} className="flex items-center gap-2 mb-2">

                      <div className="flex-1">
                        <ResumeField
                          label=""
                          value={skill}
                          path={`technicalSkills.${index}`}
                          setContent={setContent}
                        />
                      </div>

                      <Button variant="ghost" size="icon" className="h-6 w-6" aria-label="Remove skill" onClick={() =>
                        setContent({
                          type: "REMOVE_ITEM",
                          path: "technicalSkills",
                          index
                        })
                      }>
                        <X className="h-6 w-6" />
                      </Button>
                    </div>
                  ))}

                  <Button
                    className="mt-2"
                    onClick={() =>
                      setContent({
                        type: "ADD_ITEM",
                        path: "technicalSkills",
                        value: "",
                      })
                    }
                  >
                    <DiamondPlus className="h-5 w-5" />
                    Add Technical Skill

                  </Button>
                </div>

              )}
            </div>
          </Card>
        </div>
      </div>
    </main>
  );
}
